import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StickyNav } from "@/components/StickyNav";
import { Overline } from "@/components/dash/primitives";
import { Downloads } from "@/components/dash/Downloads";
import { AlphalinerWatch } from "@/components/dash/AlphalinerWatch";
import { RateSnapshot } from "@/components/dash/RateSnapshot";
import { LaneCards } from "@/components/dash/LaneCards";
import { MarketInsight } from "@/components/dash/MarketInsight";
import { MarketInsightsEditor } from "@/components/dash/MarketInsightsEditor";
import { useAuth } from "@/context/AuthContext";
import { Pencil } from "lucide-react";
import Summary from "@/components/sections/Summary";
import Lane from "@/components/sections/Lane";
import Capacity from "@/components/sections/Capacity";
import Thailand from "@/components/sections/Thailand";
import Rates from "@/components/sections/Rates";
import Reliability from "@/components/sections/Reliability";
import Carriers from "@/components/sections/Carriers";
import Macro from "@/components/sections/Macro";
import Implications from "@/components/sections/Implications";
import Operations from "@/components/sections/Operations";
import Sources from "@/components/sections/Sources";
import { Loader2, PiggyBank } from "lucide-react";

export default function Dashboard() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [error, setError] = useState(false);
  const [ca, setCa] = useState(null);
  const [mi, setMi] = useState(null);
  const [miEdit, setMiEdit] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    api.get("/sc/market-insights").then(({ data }) => {
      if (data && (data.watch || data.insight)) setMi(data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setB(null);
    const path = id ? `/reports/${id}` : "/briefing";
    api.get(path).then(({ data }) => setB(data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    api.get("/sc/cost-avoided-summary").then(({ data }) => setCa(data)).catch(() => {});
  }, []);

  if (error) return <div className="min-h-screen flex items-center justify-center text-slate-500">Failed to load briefing.</div>;
  if (!b)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );

  const m = b.meta;

  return (
    <div className="min-h-screen bg-background">
      <StickyNav sections={b.sections} />

      {/* Report header */}
      <div className="report-header border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              <Overline>{m.eyebrow}</Overline>
              <p className="text-sm text-accent font-medium mt-6 font-num uppercase tracking-wider">{m.kicker}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mt-3 max-w-4xl leading-[1.05]">
                {m.title}
              </h1>
              <p className="text-base sm:text-lg text-slate-600 mt-6 max-w-3xl leading-relaxed">{m.subtitle}</p>
            </div>
            <div className="no-print flex items-center gap-3 shrink-0">
              {id && (
                <span className="font-num text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1.5">
                  Archived edition
                </span>
              )}
              <Downloads report={b} />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 border border-slate-200 rounded-xl overflow-hidden mt-10" data-testid="hero-facts-row">
            {m.facts.map((f) => (
              <div key={f.label} className="bg-white p-5" data-testid={`hero-fact-${String(f.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <Overline>{f.label}</Overline>
                <div className="font-display text-lg font-semibold text-slate-900 mt-1.5" data-testid={`hero-fact-value-${String(f.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{f.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {!id && b.rate_snapshot && <RateSnapshot data={b.rate_snapshot} />}
      {!id && b.lane_cards && <LaneCards data={b.lane_cards} />}
      {!id && <AlphalinerWatch data={mi?.watch} />}

      {!id && isAdmin && (
        <div className="no-print bg-slate-900" data-testid="mi-admin-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
            <span className="text-xs text-slate-300 font-num">
              Admin · market-insight bands {mi?.meta?.updated_at ? `— last published ${new Date(mi.meta.updated_at).toLocaleDateString()}` : "— showing built-in defaults"}
            </span>
            <button onClick={() => setMiEdit(true)} data-testid="edit-market-insights-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-medium px-3 py-1.5">
              <Pencil className="w-3.5 h-3.5" /> Edit market insights
            </button>
          </div>
        </div>
      )}

      {miEdit && (
        <MarketInsightsEditor
          initial={mi}
          onClose={() => setMiEdit(false)}
          onSaved={(data) => setMi(data)}
        />
      )}

      {ca && (ca.count > 0 || ca.adjustments_total > 0) && (
        <div className="no-print border-b border-emerald-200 bg-emerald-50" data-testid="dashboard-cost-avoided">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-wrap items-center gap-x-10 gap-y-3">
            <div className="flex items-center gap-2 text-emerald-800">
              <PiggyBank className="w-5 h-5" />
              <span className="font-display font-semibold text-sm">Freight cost avoided</span>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-600/80 font-num">This month</div>
              <div className="font-display text-2xl font-bold text-emerald-700 font-num" data-testid="ca-month">
                ${Math.round(ca.this_month).toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-600/80 font-num">Year to date</div>
              <div className="font-display text-2xl font-bold text-emerald-700 font-num" data-testid="ca-ytd">
                ${Math.round(ca.ytd).toLocaleString()}
              </div>
            </div>
            <div className="text-xs text-emerald-700/70 font-num ml-auto">
              {ca.count > 0 && `from ${ca.count} invoice-audit variance${ca.count === 1 ? "" : "s"}`}
              {ca.count > 0 && ca.adjustments_total > 0 && " + "}
              {ca.adjustments_total > 0 && `EBS Quantum ($${Math.round(ca.adjustments_total).toLocaleString()})`}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-main">
        <Summary data={b.summary} num="01" title="Executive Summary" />
        <MarketInsight data={mi?.insight} />
        <Lane data={b.lane} num="02" title="Australia Lane vs. Rest of World" />
        <Capacity data={b.capacity} num="03" title="New Capacity" />
        <Thailand data={b.thailand} num="04" title="Thailand Origin Context" />
        <Rates data={b.rates} num="05" title="Freight Rates & Charter Market" />
        <Reliability data={b.reliability} num="06" title="Schedule Reliability & Capacity Tightness" />
        <Carriers data={b.carriers} num="07" title="Who's Moving Your Cargo, And Through Where" />
        <Macro data={b.macro} num="08" title="Global Drivers That Reach Your Supply Chain" />
        <Implications data={b.implications} num="09" title="What This Means For You" />
        {b.operations && <Operations data={b.operations} num="10" title="Carrier Operations & September Outlook" />}
        <Sources data={b.sources} footer={b.footer} />
      </div>

      {/* Summary-only view, printed to PDF when "Executive summary (PDF)" is chosen */}
      <div id="print-summary" className="max-w-3xl mx-auto px-8 py-10">
        <Overline>{m.eyebrow}</Overline>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-2">{m.title}</h1>
        <p className="font-num text-xs text-slate-500 mt-2">
          {m.facts.map((f) => `${f.label}: ${f.value}`).join("  ·  ")}
        </p>
        <h2 className="font-display text-xl font-bold text-slate-900 mt-8">Executive Summary</h2>
        <p className="text-slate-700 leading-relaxed mt-3">{b.summary.lede}</p>
        <div className="grid grid-cols-2 gap-4 mt-6">
          {b.summary.stats.map((s, i) => (
            <div key={i} className="border border-slate-200 rounded-lg p-4">
              <div className="font-display text-3xl font-bold text-slate-900">{s.value}</div>
              <div className="font-num text-xs text-slate-600 mt-1">{s.indicator}</div>
              <p className="text-xs text-slate-600 mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-slate-600 mt-6 leading-relaxed">{b.summary.note}</p>
      </div>
    </div>
  );
}
